import { siteText } from '../siteText'
import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, CircleSlash, Film, LayoutGrid, LoaderCircle, RotateCcw, SlidersHorizontal } from 'lucide-react'
import './StatusFilter.css'
import { IconTooltip } from './IconTooltip'

const options = [
  { value: 'review_pending', label: 'Review', icon: Film },
  { value: '', label: 'All statuses', icon: LayoutGrid },
  { value: 'rendering', label: 'Rendering', icon: LoaderCircle },
  { value: 'changes_requested', label: 'Changes requested', icon: RotateCcw },
  { value: 'rejected', label: 'Rejected', icon: CircleSlash },
]

const finalOptions = [
  { value: 'final_review', label: 'Final review', icon: Film },
  { value: 'complete', label: 'Approved finals', icon: Check },
  { value: 'changes_requested', label: 'Changes requested', icon: RotateCcw },
  { value: '', label: 'All finished videos', icon: LayoutGrid },
]

const calendarOptions = [options[0], finalOptions[0], finalOptions[1], ...options.slice(1)]

const editorOptions = [
  { value: '', label: 'All edits', icon: LayoutGrid },
  { value: 'approved', label: 'Ready to edit', icon: Film },
  { value: 'editing', label: 'Editing', icon: Film },
  { value: 'changes_requested', label: 'Changes requested', icon: RotateCcw },
]

export function StatusFilter({ value, onChange, iconOnly = false, scope = 'review' }: { value: string; onChange: (value: string) => void; iconOnly?: boolean; scope?: 'review' | 'editor' | 'final' | 'calendar' }) {
  const choices = scope === 'editor' ? editorOptions : scope === 'final' ? finalOptions : scope === 'calendar' ? calendarOptions : options
  const id = useId()
  const trigger = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const selected = choices.findIndex(option => option.value === value)
  const [focused, setFocused] = useState(Math.max(0, selected))
  function open() {
    const rect = trigger.current?.getBoundingClientRect()
    if (!rect) return
    setFocused(Math.max(0, selected))
    setPosition({ top: Math.min(rect.bottom + 8, window.innerHeight - 218), left: Math.max(8, Math.min(rect.right - 216, window.innerWidth - 224)) })
  }
  function close(restore = false) { setPosition(null); if (restore) trigger.current?.focus() }
  useEffect(() => {
    if (!position) return
    menu.current?.focus()
    const outside = (event: PointerEvent) => { if (!menu.current?.contains(event.target as Node) && !trigger.current?.contains(event.target as Node)) close() }
    const resize = () => close()
    document.addEventListener('pointerdown', outside)
    window.addEventListener('resize', resize)
    return () => { document.removeEventListener('pointerdown', outside); window.removeEventListener('resize', resize) }
  }, [position])
  function choose(index: number) { onChange(choices[index].value); close(true) }
  const triggerButton = <button ref={trigger} className="status-filter-trigger" type="button" aria-label={siteText(`Filter by Status: ${choices[selected]?.label || 'All Statuses'}`)} aria-haspopup="listbox" aria-expanded={Boolean(position)} aria-controls={position ? id : undefined} onClick={() => position ? close() : open()} onKeyDown={event => { if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); open() } }}>{iconOnly ? <SlidersHorizontal size={18} /> : <><span>{siteText(choices[selected]?.label || 'All statuses')}</span><ChevronDown size={14} /></>}</button>
  return <div className={`status-filter ${iconOnly ? 'status-filter-icon' : ''}`}>{iconOnly ? <IconTooltip label="Filter clips" side="bottom">{triggerButton}</IconTooltip> : triggerButton}
    {position ? createPortal(<div ref={menu} id={id} role="listbox" aria-label="Filter by Status" aria-activedescendant={`${id}-${focused}`} tabIndex={-1} className="status-filter-menu" style={position} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node)) close() }} onKeyDown={event => {
      if (event.key === 'Escape') { event.preventDefault(); close(true) }
      else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); setFocused(current => (current + (event.key === 'ArrowDown' ? 1 : -1) + choices.length) % choices.length) }
      else if (event.key === 'Home' || event.key === 'End') { event.preventDefault(); setFocused(event.key === 'Home' ? 0 : choices.length - 1) }
      else if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); choose(focused) }
      else if (event.key.length === 1) { const index = choices.findIndex(option => option.label.toLowerCase().startsWith(event.key.toLowerCase())); if (index >= 0) setFocused(index) }
    }}>{choices.map(({ value: optionValue, label, icon: Icon }, index) => <div key={optionValue} id={`${id}-${index}`} role="option" aria-selected={value === optionValue} className={`status-filter-option ${focused === index ? 'is-focused' : ''}`} onPointerMove={() => setFocused(index)} onMouseDown={event => event.preventDefault()} onClick={() => choose(index)}><Icon size={15} strokeWidth={1.6} /><span>{siteText(label)}</span>{value === optionValue ? <Check size={14} /> : null}</div>)}</div>, document.body) : null}
  </div>
}
